import { useParams } from 'react-router-dom';
import { useSeoMeta } from '@unhead/react';
import { nip19 } from 'nostr-tools';
import { useAuthor } from '@/hooks/useAuthor';
import { genUserName } from '@/lib/genUserName';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import { useCardLists } from '@/hooks/useCardLists';
import { Deck } from '@/lib/nostr/deck';
import { useState } from 'react';
import { DeckCard } from '@/components/DeckCard';
import { ShareListDialog } from '@/components/ShareListDialog';
import { User } from 'lucide-react';

const ProfilePage = () => {
  const { npub: profileId } = useParams<{ npub: string }>();
  let pubkey = '';

  if (profileId) {
    if (profileId.startsWith('npub1')) {
      try {
        const decoded = nip19.decode(profileId);
        if (decoded.type === 'npub') {
          pubkey = decoded.data;
        }
      } catch (e) {
        // Invalid npub, treat as invalid. pubkey remains empty.
        console.error("Invalid npub:", e);
      }
    } else if (profileId.match(/^[0-9a-fA-F]{64}$/)) {
      // It's a hex key
      pubkey = profileId;
    }
  }

  const { data: author, isLoading: isLoadingAuthor } = useAuthor(pubkey);
  const { data: lists, isLoading: isLoadingLists } = useCardLists(pubkey);
  const [isShareDialogOpen, setIsShareDialogOpen] = useState(false);
  const [selectedList, setSelectedList] = useState<Deck | null>(null);

  const handleShareClick = (list: Deck) => {
    setSelectedList(list);
    setIsShareDialogOpen(true);
  };

  const metadata = author?.metadata;
  const userName = metadata?.name || genUserName(pubkey);

  useSeoMeta({
    title: `${userName}'s Profile - Surveil`,
    description: `View ${userName}'s profile and saved Magic: The Gathering card lists on Surveil.`,
  });

  return (
    <main className="pb-8">
      {isLoadingAuthor ? (
        <Skeleton className="h-48 w-full" />
      ) : (
        <div
          className="h-48 bg-cover bg-center"
          style={{
            backgroundImage: metadata?.banner ? `url(${metadata.banner})` : 'none',
            background: metadata?.banner ? `url(${metadata.banner})` : 'linear-gradient(135deg, hsl(var(--primary)) 0%, hsl(var(--accent)) 100%)'
          }}
        />
      )}
      <div className="container mx-auto px-4">
        {/* Profile Header */}
        <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6 -mt-16 sm:-mt-12 mb-8">
          {isLoadingAuthor ? (
            <Skeleton className="h-28 w-28 sm:h-32 sm:w-32 rounded-full" />
          ) : (
            <Avatar className="h-28 w-28 sm:h-32 sm:w-32 border-4 border-background">
              <AvatarImage src={metadata?.picture} alt={userName} />
              <AvatarFallback>
                <User className="h-8 w-8" />
              </AvatarFallback>
            </Avatar>
          )}
          <div className="space-y-1 text-center sm:text-left sm:pt-2 sm:pt-12">
            {isLoadingAuthor ? (
              <>
                <Skeleton className="h-8 w-48" />
                <Skeleton className="h-4 w-64" />
              </>
            ) : (
              <>
                <div className="space-y-0.5 sm:space-y-1">
                  <h2 className={`text-3xl font-bold text-foreground leading-tight ${metadata?.nip05 ? "mt-1" : ""}`}>
                    {metadata?.display_name || userName}
                    {metadata?.nip05 && (
                      <p className="text-xs text-muted-foreground">{metadata.nip05}</p>
                    )}
                  </h2>
                </div>
                {metadata?.about && (
                  <p className="text-muted-foreground max-w-2xl mt-1 sm:mt-2">
                    {metadata.about}
                  </p>
                )}
              </>
            )}
          </div>
        </div>

        {/* Saved Lists */}
        <div className="space-y-6">
          <h3 className="text-2xl font-semibold text-foreground">My Decks</h3>
          {isLoadingLists ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Skeleton className="h-32 w-full rounded-lg" />
              <Skeleton className="h-32 w-full rounded-lg" />
            </div>
          ) : lists && lists.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {lists.map((list: Deck) => (
                <DeckCard
                  key={list.event.id}
                  deck={list}
                  variant="profile"
                  onShare={handleShareClick}
                />
              ))}
            </div>
          ) : (
            <p className="text-muted-foreground">No saved lists found.</p>
          )}
        </div>
      </div>
      {selectedList && (
        <ShareListDialog
          open={isShareDialogOpen}
          onOpenChange={setIsShareDialogOpen}
          deck={selectedList}
          deckUrl={`${window.location.origin}/list/${nip19.naddrEncode({ identifier: selectedList.event.tags.find(t => t[0] === 'd')?.[1] || '', pubkey: selectedList.event.pubkey, kind: selectedList.event.kind })}`}
        />
      )}
    </main>
  );
};

export default ProfilePage;
